import { useState, useRef, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import type { SortDirection } from "@tanstack/react-table";
import SortIcon from "./SortIcon";

const CYCLE: Array<false | "asc" | "desc"> = [false, "asc", "desc"];

interface Props {
  direction: false | SortDirection;
  onSort: (direction: false | "asc" | "desc") => void;
}

export default function SortTooltip({ direction, onSort }: Props) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const show = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  }, []);

  const cycleSort = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const idx = CYCLE.indexOf(direction);
      onSort(CYCLE[(idx + 1) % CYCLE.length]);
    },
    [direction, onSort],
  );

  const selectSort = useCallback(
    (dir: false | "asc" | "desc") => {
      onSort(dir);
      setOpen(false);
    },
    [onSort],
  );

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  }, [open]);

  return (
    <span
      ref={triggerRef}
      className="lt-sort-trigger"
      onClick={cycleSort}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <SortIcon direction={direction} />
      {open &&
        createPortal(
          <div
            className="lt-sort-tooltip"
            style={{ top: pos.top, left: pos.left }}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <div className="lt-sort-tooltip__body">
              {CYCLE.map((dir) => (
                <button
                  key={String(dir)}
                  type="button"
                  className={`lt-sort-tooltip__item${direction === dir ? " lt-sort-tooltip__item--active" : ""}`}
                  onClick={(e) => { e.stopPropagation(); selectSort(dir); }}
                >
                  {dir === false ? "Без сортировки" : dir === "asc" ? "От А до Я" : "От Я до А"}
                </button>
              ))}
              <span className="lt-sort-tooltip__arrow" />
            </div>
          </div>,
          document.body,
        )}
    </span>
  );
}
